import React from "react";
import { Briefcase, GraduationCap, Award, FileWarning } from "lucide-react";
import SEO from "@/components/common/SEO";
import { CURRICULUM } from "@/data/staticContent";

export default function Curriculum() {
    return (
        <>
            <SEO
                title="Currículum del Adicto · LinkedIn del Autoengaño"
                description="20 años de experiencia en evasión, recaídas, promesas rotas y reinvenciones del mismo error. El CV que nadie debería repetir."
                path="/curriculum"
                keywords="currículum adicto, LinkedIn autoengaño, experiencia adicción, recaída"
            />

            <section className="grain relative py-20 md:py-28 border-b border-[var(--line)]">
                <div className="container-x">
                    <p className="eyebrow mb-6">Hoja de vida · 08</p>
                    <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tight mb-6">
                        Currículum del{" "}
                        <span className="text-[var(--danger)]">Adicto</span>
                    </h1>
                    <p className="font-hand text-2xl md:text-3xl text-[var(--warning)] -rotate-2 inline-block mb-6">
                        LinkedIn del Autoengaño.
                    </p>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-2xl leading-relaxed">
                        Lo que nunca te mostrarían en un CV oficial. La carrera
                        completa, con sus ascensos al fondo.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24" data-testid="cv-content">
                <div className="container-x max-w-4xl">
                    {/* Header card */}
                    <article className="dossier mb-10">
                        <div className="dossier-header">
                            <span>RESUMEN EJECUTIVO</span>
                            <span className="text-[var(--danger)]">CONFIDENCIAL</span>
                        </div>
                        <div className="dossier-content">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-2">
                                Cargo actual
                            </p>
                            <h2 className="font-display text-2xl md:text-3xl text-[var(--paper-2)] mb-4 leading-tight">
                                {CURRICULUM.cargo}
                            </h2>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-2 mt-6">
                                Antigüedad en el rubro
                            </p>
                            <p className="font-mono text-sm text-[var(--paper)]">
                                {CURRICULUM.antiguedad}
                            </p>
                        </div>
                    </article>

                    {/* Experiencia */}
                    <article className="dossier mb-10">
                        <div className="dossier-header">
                            <span><Briefcase className="inline w-3 h-3 mr-2" />EXPERIENCIA DESTACADA</span>
                            <span>1/4</span>
                        </div>
                        <ul className="dossier-content space-y-3">
                            {CURRICULUM.experiencia.map((e, i) => (
                                <li
                                    key={i}
                                    className="font-mono text-sm text-[var(--paper)] flex gap-3"
                                    data-testid={`cv-exp-${i}`}
                                >
                                    <span className="text-[var(--danger)] font-mono text-xs uppercase tracking-[0.2em] flex-shrink-0 w-8">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {e}
                                </li>
                            ))}
                        </ul>
                    </article>

                    {/* Habilidades */}
                    <article className="dossier mb-10">
                        <div className="dossier-header">
                            <span><FileWarning className="inline w-3 h-3 mr-2" />HABILIDADES TÉCNICAS</span>
                            <span>2/4</span>
                        </div>
                        <div className="dossier-content space-y-5">
                            {CURRICULUM.habilidades.map((h, i) => (
                                <div key={i} data-testid={`cv-skill-${i}`}>
                                    <div className="flex justify-between mb-2">
                                        <span className="font-mono text-sm text-[var(--paper-2)]">
                                            {h.name}
                                        </span>
                                        <span className="font-mono text-xs text-[var(--danger)]">
                                            {h.level}%
                                        </span>
                                    </div>
                                    <div className="h-2 bg-[#0d0d0d] border border-[var(--line)]">
                                        <div
                                            className="h-full bg-[var(--danger)]"
                                            style={{ width: `${h.level}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </article>

                    {/* Logros */}
                    <article className="dossier mb-10">
                        <div className="dossier-header">
                            <span><Award className="inline w-3 h-3 mr-2" />LOGROS PROFESIONALES</span>
                            <span>3/4</span>
                        </div>
                        <ul className="dossier-content space-y-3">
                            {CURRICULUM.logros.map((l, i) => (
                                <li
                                    key={i}
                                    className="font-mono text-sm text-[var(--paper)] flex gap-3"
                                    data-testid={`cv-logro-${i}`}
                                >
                                    <Award className="w-4 h-4 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                    {l}
                                </li>
                            ))}
                        </ul>
                    </article>

                    {/* Educacion */}
                    <article className="dossier mb-10">
                        <div className="dossier-header">
                            <span><GraduationCap className="inline w-3 h-3 mr-2" />EDUCACIÓN</span>
                            <span>4/4</span>
                        </div>
                        <ul className="dossier-content space-y-3">
                            {CURRICULUM.educacion.map((e, i) => (
                                <li
                                    key={i}
                                    className="font-mono text-sm text-[var(--paper)] flex gap-3"
                                    data-testid={`cv-edu-${i}`}
                                >
                                    <GraduationCap className="w-4 h-4 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                    {e}
                                </li>
                            ))}
                        </ul>
                    </article>

                    {/* Referencias */}
                    <div className="border-2 border-dashed border-[var(--danger)] p-6 text-center">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-2">
                            Referencias
                        </p>
                        <p className="font-hand text-2xl text-[var(--warning)] -rotate-1">
                            {CURRICULUM.referencias}
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
